// CRM: identity resolution. Turns source records into identities, groups them into families (customers) and
// suggests likely duplicates for the manager to confirm. Pure; see crmTypes.ts and docs/crm-design.md.

import type {
  Confidence,
  CrmHolidayInvoice,
  CrmInput,
  CrmPreschoolInvoice,
  CustomerDoc,
  Identity,
  MatchReason,
  MergeSuggestion,
  RejectedPair,
} from "./crmTypes";
import {
  canonicalEmail,
  canonicalPhone,
  emailHandle,
  firstNameOf,
  houseNumberOf,
  normaliseName,
  postcodeOf,
  present,
  similarFirstNames,
  surnameOf,
  toIsoDate,
} from "./crmNormalise";

const unique = <T>(values: T[]) => Array.from(new Set(values));
const nonEmpty = (values: unknown[]) => unique(values.map(present).filter(Boolean));

/** Key of the identity an old preschool invoice belongs to when its child has no children/{id} document. */
export const preschoolInvoiceKey = (invoice: CrmPreschoolInvoice, childIds: Set<string>) =>
  invoice.childId && childIds.has(invoice.childId)
    ? `pre:${invoice.childId}`
    : `pinv:${invoice.childId || `${canonicalEmail(invoice.email)}/${normaliseName(invoice.childName)}`}`;

/** Key for one child line of a holiday invoice whose registration is not in Firestore. */
export const holidayInvoiceChildKey = (invoice: CrmHolidayInvoice, childName: string) =>
  `hinv:${canonicalEmail(invoice.email)}/${normaliseName(childName)}`;

/** One identity per source record. */
export const identitiesFrom = (input: CrmInput): Identity[] => {
  const identities: Identity[] = [];
  const registrations = new Map(input.registrations.map((r) => [r.id, r]));
  const usedRegistrations = new Set<string>();
  const childIds = new Set(input.children.map((c) => c.id));

  const childName = (r: { childFirstName?: string; childMiddleName?: string; childLastName?: string }) =>
    [r.childFirstName, r.childMiddleName, r.childLastName].map(present).filter(Boolean).join(" ");

  const fromRegistration = (r: typeof input.registrations[number] | undefined) => ({
    parentNames: nonEmpty([r?.parent1FullName, r?.parent2FullName]),
    emails: nonEmpty([r?.parent1Email, r?.email, r?.parent2Email]),
    phones: nonEmpty([r?.parent1Phone, r?.parent2Phone]),
    address: present(r?.parent1Address) || present(r?.parent2Address),
    childName: r ? childName(r) : "",
    dateOfBirth: toIsoDate(r?.childDateOfBirth),
    at: toIsoDate(r?.timeStamp),
  });

  for (const child of input.children) {
    const registration = child.registrationId ? registrations.get(child.registrationId) : undefined;
    if (registration) usedRegistrations.add(registration.id);
    const base = fromRegistration(registration);
    identities.push({
      key: `pre:${child.id}`,
      kind: "preschoolChild",
      service: "preschool",
      label: child.ignore ? "Preschool · left" : "Preschool",
      ...base,
      emails: unique([...base.emails, ...nonEmpty([child.parentEmail])]),
      childName: base.childName || child.id,
      preschool: {
        childId: child.id,
        registrationId: registration?.id ?? null,
        enrolment: registration?.enrolment ?? "",
        funding: registration?.fundingEntitlement ?? "",
        startDate: toIsoDate(registration?.startDate),
        left: !!child.ignore,
      },
    });
  }

  for (const registration of input.registrations) {
    if (usedRegistrations.has(registration.id) || registration.ignore) continue;
    identities.push({
      key: `reg:${registration.id}`,
      kind: "preschoolRegistration",
      service: "preschool",
      label: "Preschool registration",
      ...fromRegistration(registration),
      preschool: {
        childId: null,
        registrationId: registration.id,
        enrolment: registration.enrolment ?? "",
        funding: registration.fundingEntitlement ?? "",
        startDate: toIsoDate(registration.startDate),
        left: false,
      },
    });
  }

  const terms = new Map(input.holidayTerms.map((t) => [t.id, t]));
  const holidayIds = new Set<string>();
  for (const registration of input.holidayRegistrations) {
    holidayIds.add(registration.id);
    const term = terms.get(registration.termId);
    const booked = registration.form.days.filter((day) => day.choice !== "NONE");
    const dates = booked.map((day) => day.date).filter((date): date is string => !!date).sort();
    identities.push({
      key: `hol:${registration.id}`,
      kind: "holidayBooking",
      service: "holiday",
      label: `Holiday Club · ${(term?.name ?? registration.termId).replace(/^Registration form - /i, "")}`,
      parentNames: nonEmpty([registration.form.parentName]),
      emails: nonEmpty([registration.form.email]),
      phones: nonEmpty([registration.form.phone]),
      address: present(registration.form.address),
      childName: present(registration.form.childName),
      dateOfBirth: null,
      at: toIsoDate(registration.form.submittedAt),
      holiday: {
        registrationId: registration.id,
        termId: registration.termId,
        termName: (term?.name ?? registration.termId).replace(/^Registration form - /i, ""),
        fullDays: booked.filter((day) => day.choice === "FULL").length,
        halfDays: booked.filter((day) => day.choice === "HALF").length,
        firstDay: dates[0] ?? null,
        lastDay: dates[dates.length - 1] ?? null,
        cancelled: !!registration.admin?.cancelled || !!registration.removedFromForm,
        invoiceNo: registration.invoiceNo ?? null,
      },
    });
  }

  // Invoices whose child or registration is not in Firestore (e.g. imported history) still belong to someone.
  const invoiceOnly = new Map<string, Identity>();
  const addInvoiceOnly = (key: string, make: () => Identity, at: string | null) => {
    const existing = invoiceOnly.get(key);
    if (!existing) invoiceOnly.set(key, make());
    else if (at && (!existing.at || at < existing.at)) existing.at = at;
  };
  for (const invoice of input.preschoolInvoices) {
    if (invoice.cancelled) continue;
    const key = preschoolInvoiceKey(invoice, childIds);
    if (!key.startsWith("pinv:")) continue;
    addInvoiceOnly(key, () => ({
      key,
      kind: "invoiceOnly",
      service: "preschool",
      label: "Preschool invoices",
      parentNames: nonEmpty([invoice.parentName]),
      emails: nonEmpty([invoice.email]),
      phones: [],
      address: "",
      childName: present(invoice.childName),
      dateOfBirth: null,
      at: toIsoDate(invoice.issuedDate),
      preschool: { childId: invoice.childId || null, registrationId: null, enrolment: "", funding: "", startDate: null, left: true },
    }), toIsoDate(invoice.issuedDate));
  }
  for (const invoice of input.holidayInvoices) {
    if (invoice.cancelled) continue;
    for (const child of invoice.children) {
      if (holidayIds.has(child.registrationId)) continue;
      const key = holidayInvoiceChildKey(invoice, child.name);
      const dates = child.days.filter((d) => d.choice !== "NONE").map((d) => d.date).filter((d): d is string => !!d).sort();
      addInvoiceOnly(key, () => ({
        key,
        kind: "invoiceOnly",
        service: "holiday",
        label: "Holiday Club invoices",
        parentNames: nonEmpty([invoice.parentName]),
        emails: nonEmpty([invoice.email]),
        phones: [],
        address: "",
        childName: present(child.name),
        dateOfBirth: null,
        at: toIsoDate(invoice.issuedDate) ?? dates[0] ?? null,
      }), toIsoDate(invoice.issuedDate));
    }
  }
  identities.push(...invoiceOnly.values());

  for (const customer of input.customers) {
    const enquiry = customer.enquiry;
    if (!enquiry) continue;
    identities.push({
      key: `man:${customer.id}`,
      kind: "enquiry",
      service: null,
      label: "Enquiry",
      parentNames: nonEmpty([enquiry.parentName]),
      emails: nonEmpty([enquiry.email]),
      phones: nonEmpty([enquiry.phone]),
      address: present(enquiry.address),
      childName: present(enquiry.childName),
      dateOfBirth: enquiry.childDateOfBirth || null,
      at: enquiry.date || toIsoDate(customer.createdAt),
      enquiry,
    });
  }

  return identities;
};

// ---------------------------------------------------------------------------------------------------------------
// Families

export interface Cluster {
  /** The stored document id, or a deterministic id for a family nobody has stored yet. */
  id: string;
  keys: string[];
  doc: CustomerDoc | null;
  extraDocs: CustomerDoc[];
}

/** Firestore document id for a family that is not stored yet, derived from its first identity. */
export const autoCustomerId = (firstKey: string) => `c_${firstKey.replace(/[^A-Za-z0-9_-]/g, "_")}`.slice(0, 200);

export const pairId = (a: string, b: string) => {
  const [x, y] = a < b ? [a, b] : [b, a];
  return `${x}__${y}`.replace(/\//g, "_");
};

/**
 * Groups identities into families:
 * 1. every stored customer document links its members (the manager's confirmed merges and splits);
 * 2. identities sharing an email (canonical form) are the same family, unless that would join two families the
 *    manager said are different (a rejected pair between them).
 */
export const clusterIdentities = (identities: Identity[], customers: CustomerDoc[], rejected: RejectedPair[]): Cluster[] => {
  const known = new Set(identities.map((i) => i.key));
  const parent = new Map<string, string>();
  const members = new Map<string, Set<string>>();
  for (const key of known) {
    parent.set(key, key);
    members.set(key, new Set([key]));
  }
  const find = (key: string): string => {
    let root = key;
    while (parent.get(root) !== root) root = parent.get(root)!;
    parent.set(key, root);
    return root;
  };
  const partners = new Map<string, Set<string>>();
  for (const pair of rejected) {
    if (!partners.has(pair.a)) partners.set(pair.a, new Set());
    if (!partners.has(pair.b)) partners.set(pair.b, new Set());
    partners.get(pair.a)!.add(pair.b);
    partners.get(pair.b)!.add(pair.a);
  }
  const blocked = (rootA: string, rootB: string) => {
    const [small, large] = members.get(rootA)!.size <= members.get(rootB)!.size
      ? [members.get(rootA)!, members.get(rootB)!] : [members.get(rootB)!, members.get(rootA)!];
    for (const key of small) {
      for (const other of partners.get(key) ?? []) if (large.has(other)) return true;
    }
    return false;
  };
  const union = (a: string, b: string, force: boolean) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA === rootB || (!force && blocked(rootA, rootB))) return;
    const [keep, drop] = members.get(rootA)!.size >= members.get(rootB)!.size ? [rootA, rootB] : [rootB, rootA];
    parent.set(drop, keep);
    for (const key of members.get(drop)!) members.get(keep)!.add(key);
    members.delete(drop);
  };

  const docsInOrder = [...customers].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  for (const customer of docsInOrder) {
    const keys = [...customer.members, ...(customer.enquiry ? [`man:${customer.id}`] : [])].filter((k) => known.has(k));
    for (let i = 1; i < keys.length; i++) union(keys[0]!, keys[i]!, true);
  }

  const byEmail = new Map<string, string[]>();
  for (const identity of identities) {
    for (const email of unique(identity.emails.map(canonicalEmail).filter(Boolean))) {
      byEmail.set(email, [...(byEmail.get(email) ?? []), identity.key]);
    }
  }
  for (const keys of byEmail.values()) {
    for (let i = 1; i < keys.length; i++) union(keys[0]!, keys[i]!, false);
  }

  const clusters: Cluster[] = [];
  for (const [, set] of members) {
    const keys = [...set].sort();
    const docs = docsInOrder.filter((customer) =>
      customer.members.some((k) => set.has(k)) || set.has(`man:${customer.id}`));
    clusters.push({ id: docs[0]?.id ?? autoCustomerId(keys[0]!), keys, doc: docs[0] ?? null, extraDocs: docs.slice(1) });
  }
  return clusters.sort((a, b) => a.id.localeCompare(b.id));
};

// ---------------------------------------------------------------------------------------------------------------
// Duplicate suggestions

interface Features {
  emails: Set<string>;
  handles: Set<string>;
  phones: Set<string>;
  postcodes: Set<string>;
  houses: Set<string>;
  parents: Set<string>;
  parentSurnames: Set<string>;
  children: Set<string>;
  childFirsts: { first: string; surname: string }[];
}

const featuresOf = (identities: Identity[]): Features => {
  const f: Features = {
    emails: new Set(), handles: new Set(), phones: new Set(), postcodes: new Set(), houses: new Set(),
    parents: new Set(), parentSurnames: new Set(), children: new Set(), childFirsts: [],
  };
  for (const identity of identities) {
    for (const email of identity.emails.map(canonicalEmail).filter(Boolean)) {
      f.emails.add(email);
      const handle = emailHandle(email);
      if (handle.length >= 5) f.handles.add(handle);
    }
    identity.phones.map(canonicalPhone).filter(Boolean).forEach((p) => f.phones.add(p));
    const postcode = postcodeOf(identity.address);
    if (postcode) {
      f.postcodes.add(postcode);
      const house = houseNumberOf(identity.address);
      if (house) f.houses.add(`${house} ${postcode}`);
    }
    for (const name of identity.parentNames) {
      const full = normaliseName(name);
      if (full.includes(" ")) f.parents.add(full);
      const surname = surnameOf(name);
      if (surname) f.parentSurnames.add(surname);
    }
    if (identity.childName) {
      const full = normaliseName(identity.childName);
      if (full.includes(" ")) f.children.add(full);
      const first = firstNameOf(identity.childName);
      if (first) f.childFirsts.push({ first, surname: surnameOf(identity.childName) });
    }
  }
  return f;
};

const shared = (a: Set<string>, b: Set<string>) => [...a].filter((x) => b.has(x));

/** Combined evidence: 1 - product of (1 - weight). Independent weak signals add up, none reaches 1. */
const combine = (reasons: MatchReason[]) => 1 - reasons.reduce((p, r) => p * (1 - r.weight), 1);

export const WEIGHTS = {
  phone: 0.7,
  childFullName: 0.6,
  emailHandle: 0.5,
  childSimilarName: 0.45,
  parentFullName: 0.45,
  houseAndPostcode: 0.45,
  postcode: 0.35,
  childFirstName: 0.2,
  parentSurname: 0.15,
};

export const confidenceOf = (score: number): Confidence => (score >= 0.85 ? "high" : score >= 0.6 ? "medium" : "low");

/** Why two families look like the same one, with weights. */
export const matchReasons = (a: Features, b: Features): MatchReason[] => {
  const reasons: MatchReason[] = [];
  const phones = shared(a.phones, b.phones);
  if (phones.length) reasons.push({ label: `Same phone ${phones[0]}`, weight: WEIGHTS.phone });
  const handles = shared(a.handles, b.handles);
  if (handles.length && !shared(a.emails, b.emails).length) {
    reasons.push({ label: `Similar email (${handles[0]}@…)`, weight: WEIGHTS.emailHandle });
  }
  const parents = shared(a.parents, b.parents);
  if (parents.length) reasons.push({ label: `Same parent name`, weight: WEIGHTS.parentFullName });
  else if (shared(a.parentSurnames, b.parentSurnames).length) reasons.push({ label: "Same parent surname", weight: WEIGHTS.parentSurname });

  const children = shared(a.children, b.children);
  if (children.length) {
    reasons.push({ label: `Same child name`, weight: WEIGHTS.childFullName });
  } else {
    let best: MatchReason | null = null;
    for (const x of a.childFirsts) {
      for (const y of b.childFirsts) {
        if (!similarFirstNames(x.first, y.first)) continue;
        const sameSurname = !!x.surname && x.surname === y.surname;
        const reason = sameSurname
          ? { label: "Similar child name", weight: WEIGHTS.childSimilarName }
          : { label: "Same child first name", weight: WEIGHTS.childFirstName };
        if (!best || reason.weight > best.weight) best = reason;
      }
    }
    if (best) reasons.push(best);
  }

  const houses = shared(a.houses, b.houses);
  if (houses.length) reasons.push({ label: `Same address (${houses[0]})`, weight: WEIGHTS.houseAndPostcode });
  else {
    const postcodes = shared(a.postcodes, b.postcodes);
    if (postcodes.length) reasons.push({ label: `Same postcode ${postcodes[0]}`, weight: WEIGHTS.postcode });
  }
  return reasons;
};

/**
 * Pairs of families that are probably one family (different emails, so not linked automatically), best first.
 * Families compared are only those sharing at least one phone, email handle, name or postcode (blocking), and
 * pairs the manager rejected never come back.
 */
export const suggestMerges = (
  clusters: Cluster[],
  identitiesByKey: Map<string, Identity>,
  rejected: RejectedPair[],
  minScore = 0.4
): MergeSuggestion[] => {
  const features = clusters.map((c) => featuresOf(c.keys.map((k) => identitiesByKey.get(k)!).filter(Boolean)));
  const rejectedIds = new Set(rejected.map((p) => pairId(p.a, p.b)));
  const blocks = new Map<string, number[]>();
  features.forEach((f, index) => {
    const tokens = [
      ...[...f.phones].map((x) => `p:${x}`),
      ...[...f.handles].map((x) => `h:${x}`),
      ...[...f.parents].map((x) => `n:${x}`),
      ...[...f.parentSurnames].map((x) => `s:${x}`),
      ...f.childFirsts.map((x) => `c:${x.first.slice(0, 3)}`),
      ...[...f.postcodes].map((x) => `z:${x}`),
    ];
    for (const token of unique(tokens)) blocks.set(token, [...(blocks.get(token) ?? []), index]);
  });

  const seen = new Set<string>();
  const suggestions: MergeSuggestion[] = [];
  for (const indexes of blocks.values()) {
    if (indexes.length > 60) continue; // a token everyone shares (a common surname) says nothing on its own
    for (let i = 0; i < indexes.length; i++) {
      for (let j = i + 1; j < indexes.length; j++) {
        const x = indexes[i]!;
        const y = indexes[j]!;
        const id = `${x}:${y}`;
        if (seen.has(id)) continue;
        seen.add(id);
        const a = clusters[x]!;
        const b = clusters[y]!;
        const crossRejected = a.keys.some((ka) => b.keys.some((kb) => rejectedIds.has(pairId(ka, kb))));
        if (crossRejected) continue;
        const reasons = matchReasons(features[x]!, features[y]!);
        const score = combine(reasons);
        if (score < minScore) continue;
        suggestions.push({
          id: pairId(a.id, b.id),
          a: a.id,
          b: b.id,
          score,
          confidence: confidenceOf(score),
          reasons: reasons.sort((r, s) => s.weight - r.weight),
          pair: [a.keys[0]!, b.keys[0]!],
        });
      }
    }
  }
  return suggestions.sort((s, t) => t.score - s.score || s.id.localeCompare(t.id));
};

// ---------------------------------------------------------------------------------------------------------------
// Children within a family

export interface ChildGroup {
  key: string;
  identities: Identity[];
}

/** Two appearances of a child in one family are the same child when their first names agree and birthdays do not clash. */
export const sameChild = (a: Identity, b: Identity) => {
  if (a.dateOfBirth && b.dateOfBirth && a.dateOfBirth !== b.dateOfBirth) return false;
  if (normaliseName(a.childName) === normaliseName(b.childName)) return true;
  return similarFirstNames(firstNameOf(a.childName), firstNameOf(b.childName));
};

/**
 * Groups a family's identities into children. Manual labels (customer.childGroups) win: labelled identities only
 * join the same label. Identities without a child name are not children (their money still counts for the family).
 */
export const groupChildren = (identities: Identity[], labels: Record<string, string> = {}): ChildGroup[] => {
  const withChild = identities.filter((i) => i.childName).sort((a, b) => a.key.localeCompare(b.key));
  const groups: Identity[][] = [];
  const labelGroup = new Map<string, number>();
  for (const identity of withChild) {
    const label = labels[identity.key];
    if (!label) continue;
    if (!labelGroup.has(label)) {
      labelGroup.set(label, groups.length);
      groups.push([]);
    }
    groups[labelGroup.get(label)!]!.push(identity);
  }
  const labelOf = new Map([...labelGroup].map(([label, index]) => [index, label]));
  for (const identity of withChild) {
    if (labels[identity.key]) continue;
    let index = groups.findIndex((members, i) => !labelOf.has(i) && members.some((other) => sameChild(identity, other)));
    if (index < 0) {
      index = groups.length;
      groups.push([]);
    }
    groups[index]!.push(identity);
  }
  return groups
    .map((members, index) => ({ key: labelOf.get(index) ?? members.map((m) => m.key).sort()[0] ?? "", identities: members }))
    .filter((g) => g.identities.length);
};
